<script setup lang="ts">
    import { computed, onMounted, ref, watch } from 'vue';
    import { __ } from '@/js/stores/expressions';
    import { useElementFocus } from '@/js/composables/useElementFocus';
    import SettingsPageLayout from '@/js/components/settings/SettingsPageLayout.vue';
    import AutoComplete from '@/js/components/autoComplete/AutoComplete.vue';
    import type { Option } from '@/js/components/autoComplete/types';
    import EchoButton from '@/js/components/EchoButton.vue';
    import FormGroup from '@/js/components/FormGroup.vue';
    import CheckboxInput from '@/js/components/CheckboxInput.vue';
    import { useSuccessDialog } from '@/js/stores/success-dialog';
    import {
        useQueryGetCompanies,
        useQueryGetExternalSystemsForTenant,
        useQueryGetImportIntegrationConfigOptions,
        useMutationImportIntegrationConfig,
    } from '@/js/services/api/backOffice/backOfficeQueries';

    const successDialogStore = useSuccessDialog();
    const { setFocus } = useElementFocus();

    onMounted(() => {
        setFocus('#import-integration-config-tenant');
    });

    const { data: companiesData } = useQueryGetCompanies();

    const selectedTenantId = ref('');
    const selectedSystemId = ref('');
    const tenantSearch = ref<string | null>(null);
    const systemSearch = ref<string | null>(null);

    const { data: externalSystemsData } = useQueryGetExternalSystemsForTenant(selectedTenantId);

    const companies = computed(() =>
        (companiesData.value ?? []).filter((c) => c.tenantId !== null)
    );

    const tenantOptions = computed<Option[]>(() =>
        companies.value.map((c) => ({ name: c.name, value: c.tenantId! }))
    );

    const systemOptions = computed<Option[]>(() =>
        (externalSystemsData.value ?? []).map((s) => ({ name: s.systemName, value: s.systemId }))
    );

    const onTenantSelect = (option: Option) => {
        selectedTenantId.value = option.value;
    };

    const onTenantClear = () => {
        selectedTenantId.value = '';
    };

    const onSystemSelect = (option: Option) => {
        selectedSystemId.value = option.value;
    };

    const onSystemClear = () => {
        selectedSystemId.value = '';
    };

    watch(selectedTenantId, () => {
        selectedSystemId.value = '';
        systemSearch.value = null;
        selectedConfigs.value = [];
    });

    const { data: optionsData } = useQueryGetImportIntegrationConfigOptions(
        selectedTenantId,
        selectedSystemId
    );
    const options = computed(() => optionsData.value ?? []);

    const selectedConfigs = ref<string[]>([]);
    const initializedConfigsSystemId = ref('');

    watch(optionsData, (newOptions) => {
        if (
            !newOptions ||
            !selectedSystemId.value ||
            initializedConfigsSystemId.value === selectedSystemId.value
        ) {
            return;
        }
        selectedConfigs.value = newOptions.filter((o) => o.isSelected).map((o) => o.name);
        initializedConfigsSystemId.value = selectedSystemId.value;
    });

    watch(selectedSystemId, () => {
        selectedConfigs.value = [];
        initializedConfigsSystemId.value = '';
    });

    const toggleConfig = (name: string, checked: boolean) => {
        if (checked) {
            if (!selectedConfigs.value.includes(name)) {
                selectedConfigs.value = [...selectedConfigs.value, name];
            }
        } else {
            selectedConfigs.value = selectedConfigs.value.filter((c) => c !== name);
        }
    };

    const { mutateAsync: importIntegrationConfigMutation, isPending } =
        useMutationImportIntegrationConfig();

    const canSubmit = computed(
        () =>
            !!selectedTenantId.value && !!selectedSystemId.value && selectedConfigs.value.length > 0
    );

    const submit = async () => {
        if (!canSubmit.value) return;
        await importIntegrationConfigMutation({
            tenantId: selectedTenantId.value,
            externalSystemId: selectedSystemId.value,
            configs: selectedConfigs.value,
        });
        successDialogStore.openDialog({
            message: __('back-office-import-integration-config-success'),
        });
    };
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">{{ __('back-office-import-integration-config') }}</h1>
        </template>
        <template #content>
            <form class="w-lg max-w-full" @submit.prevent="submit">
                <FormGroup>
                    <AutoComplete
                        v-model="tenantSearch"
                        input-id="import-integration-config-tenant"
                        :label="__('back-office-select-customer')"
                        :options="tenantOptions"
                        @select="onTenantSelect"
                        @clear="onTenantClear"
                    />
                    <template v-if="selectedTenantId">
                        <AutoComplete
                            v-model="systemSearch"
                            input-id="import-integration-config-system"
                            :label="__('back-office-import-integration-config-select-system')"
                            :options="systemOptions"
                            @select="onSystemSelect"
                            @clear="onSystemClear"
                        />
                        <template v-if="selectedSystemId && options.length > 0">
                            <fieldset>
                                <legend class="text-overline mb-1 block">
                                    {{ __('back-office-import-integration-config-options') }}
                                </legend>
                                <div class="flex flex-col gap-1">
                                    <CheckboxInput
                                        v-for="option in options"
                                        :id="`import-integration-config-option-${option.name}`"
                                        :key="option.name"
                                        :model-value="selectedConfigs.includes(option.name)"
                                        :disabled="!option.isEnabled"
                                        @update:model-value="
                                            toggleConfig(option.name, $event ?? false)
                                        "
                                    >
                                        {{ option.name }}
                                    </CheckboxInput>
                                </div>
                            </fieldset>
                        </template>
                    </template>
                </FormGroup>
                <div class="mt-8 flex flex-col gap-3 w-fit">
                    <EchoButton
                        type="submit"
                        variant="primary"
                        :disabled="!canSubmit"
                        :loading="isPending"
                    >
                        {{ __('back-office-import-integration-config-sync') }}
                    </EchoButton>
                </div>
            </form>
        </template>
    </SettingsPageLayout>
</template>
